import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { motion } from "framer-motion";
import { 
  Sparkles, Coins, Brain, Clock, 
  Users, LayoutGrid, CalendarHeart, 
  MessageSquare, ArrowRight, Star, Heart, X,
  Instagram, Facebook, Twitter
} from "lucide-react";
import logoMain from "../assets/logo_main.png";
import ringsImage from "../assets/wedding_rings_beautiful.png";

const TESTIMONIALS = [
  { names: "Marie & Julien", status: "Mariés en mai 2025", rating: 5, text: "Enfin un outil qui nous a permis de tout gérer sans nous disputer une seule fois. Le plan de table a été un jeu d'enfant !" },
  { names: "Sophie & Thomas", status: "En pleine planification", rating: 4, text: "Le budget partagé en temps réel a changé notre vie. On sait exactement où on en est à chaque étape." },
  { names: "Léa & Alexandre", status: "Mariage prévu en juin 2026", rating: 5, text: "L'assistant IA nous a bluffés. Il a trouvé des idées géniales pour notre thème bohème et structuré mon discours !" },
  { names: "Chloé & Maxime", status: "Recherche de prestataires", rating: 5, text: "Le plan de table 3D immersif est incroyable. Pouvoir placer ses invités en visualisant la salle a retiré tout notre stress." },
  { names: "Camille & Lucas", status: "Mariés en août 2024", rating: 4, text: "Très pratique pour centraliser les réponses des invités. L'interface est intuitive et le suivi des prestataires est un vrai plus." },
  { names: "Emma & Hugo", status: "Préparatifs en cours", rating: 5, text: "La checklist personnalisée générée par l'IA est d'une précision redoutable. On n'a absolument rien oublié !" },
  { names: "Juliette & Antoine", status: "Mariés en juillet 2025", rating: 4, text: "L'outil de budget est super bien pensé. Il manque peut-être juste une exportation PDF, mais le reste est magnifique." },
  { names: "Sarah & David", status: "Début d'organisation", rating: 5, text: "On cherchait une app esthétique et intelligente. Ceremind dépasse nos attentes, c'est notre QG quotidien." }
];

export default function Landing() {
  const { user } = useAuth();
  const [legalModal, setLegalModal] = useState(null);

  // Animations Framer Motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 15 }
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-parchment text-charcoal selection:bg-rosepoudre selection:text-charcoal font-sans overflow-x-hidden">
      
      {/* Navigation Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 lg:px-16 py-4 flex items-center justify-between bg-white/80 backdrop-blur-md border-b border-border-cream/40">
        <Link to="/" className="flex items-center gap-2">
          <img src={logoMain} alt="Ceremind Logo" className="h-10 lg:h-11 w-auto object-contain" />
        </Link>
        
        {/* Navigation Links - Desktop */}
        <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-widest font-semibold text-charcoal/70">
          <button onClick={() => scrollToSection("problems")} className="hover:text-terracotta transition-colors">Le Défi</button>
          <button onClick={() => scrollToSection("how-it-works")} className="hover:text-terracotta transition-colors">Méthode</button>
          <button onClick={() => scrollToSection("features")} className="hover:text-terracotta transition-colors">Outils</button>
          <button onClick={() => scrollToSection("testimonials")} className="hover:text-terracotta transition-colors">Témoignages</button>
        </nav>

        <div className="flex items-center gap-4">
          {user ? (
            <Link 
              to="/app" 
              className="btn-sage px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm flex items-center gap-1.5"
            >
              Mon Tableau de bord <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <>
              <Link 
                to="/login" 
                className="text-xs uppercase tracking-wider font-bold text-charcoal hover:text-terracotta transition-colors px-2 py-1"
              >
                Connexion
              </Link>
              <Link 
                to="/register" 
                className="btn-terracotta px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm hover:scale-102 transition-transform"
              >
                Commencer
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24">
        
        {/* Hero Section */}
        <section className="relative px-6 lg:px-16 py-16 lg:py-24 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div 
            className="lg:col-span-7 space-y-8"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div 
              variants={itemVariants}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rosepoudre/30 border border-rosepoudre text-xs font-semibold text-charcoal/80"
            >
              <Heart className="w-3 h-3 text-coral fill-coral/30" />
              <span>L'espace partagé des futurs mariés</span>
            </motion.div>
            
            <motion.h1 
              variants={itemVariants}
              className="font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight text-charcoal font-light"
            >
              Votre mariage sans stress,<br />
              <span className="italic text-terracotta font-normal">organisé à deux avec sérénité</span>
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="max-w-xl text-base lg:text-lg text-charcoal-soft font-light leading-relaxed text-balance"
            >
              Ceremind est l’espace partagé qui vous permet de tout organiser ensemble : 
              invités, budget, plan de table, souvenirs… sans disputes ni surcharge mentale.
            </motion.p>
            
            <motion.div 
              variants={itemVariants}
              className="pt-4 flex flex-wrap gap-4"
            >
              {user ? (
                <Link 
                  to="/app" 
                  className="btn-terracotta px-8 py-4 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-transform"
                >
                  Accéder à mon espace couple
                </Link>
              ) : (
                <Link 
                  to="/register" 
                  className="btn-terracotta px-8 py-4 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-transform"
                >
                  Créer notre espace couple
                </Link>
              )}
              <button 
                onClick={() => scrollToSection("how-it-works")}
                className="btn-outline-sage px-8 py-4 rounded-full text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:scale-[1.02] transition-transform"
              >
                Voir comment ça marche
              </button>
            </motion.div>
          </motion.div>
          
          {/* Hero Image overlapping cards layout */}
          <motion.div 
            className="lg:col-span-5 relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <div className="relative mx-auto max-w-[420px] lg:max-w-none aspect-[4/5] rounded-3xl overflow-hidden border border-border-cream shadow-2xl p-2 bg-white">
              <img 
                src="https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&q=80&w=800" 
                alt="Mariage chaleureux sous des lampions" 
                className="w-full h-full object-cover rounded-2xl" 
              />
              <div className="absolute inset-2 bg-gradient-to-t from-black/25 via-transparent to-transparent pointer-events-none rounded-2xl" />
            </div>
            
            {/* Small floating badge */}
            <motion.div 
              className="absolute -bottom-6 -left-6 bg-white border border-border-cream p-5 rounded-2xl shadow-xl hidden md:block max-w-[200px]"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              <div className="flex gap-1 mb-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <Star key={i} className="w-3.5 h-3.5 fill-terracotta text-terracotta" />
                ))}
              </div>
              <div className="font-serif text-sm text-charcoal font-semibold">100% de complicité</div>
              <div className="text-[10px] text-charcoal-soft font-light mt-1">Zéro stress, plus de bonheur partagé.</div>
            </motion.div>
          </motion.div>
        </section>

        {/* Section 2 - Problèmes */}
        <section id="problems" className="py-24 bg-rosepoudre/20 border-t border-b border-border-cream/40">
          <div className="max-w-4xl mx-auto text-center mb-16 px-6 space-y-4">
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-charcoal leading-tight">
              Le mariage ne devrait pas être une source de stress
            </h2>
            <p className="text-charcoal-soft font-light max-w-xl mx-auto">
              Entre la liste des invités, le budget, le plan de table et les prestataires… il est facile de s’emporter.
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { t: "Disputes sur l’argent", d: "Les questions budgétaires sont la première cause de tensions lors de l'organisation.", icon: Coins },
              { t: "Oubli des détails", d: "Un contrat mal lu ou un prestataire non relancé peut gâcher de précieux instants.", icon: Clock },
              { t: "Charge mentale déséquilibrée", d: "L'un des conjoints finit souvent par porter seul le poids des décisions.", icon: Brain },
              { t: "Difficulté à centraliser", d: "Des e-mails perdus, des notes volantes et des fichiers Excel éparpillés partout.", icon: LayoutGrid }
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.t} className="bg-white border border-border-cream p-8 rounded-2xl shadow-sm hover:border-terracotta transition-colors duration-300">
                  <div className="w-10 h-10 rounded-xl bg-rosepoudre/45 flex items-center justify-center mb-6">
                    <Icon className="w-5 h-5 text-coral" />
                  </div>
                  <h3 className="font-serif text-lg font-semibold text-charcoal mb-2">{p.t}</h3>
                  <p className="text-xs text-charcoal-soft font-light leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 3 - Comment ça marche */}
        <section id="how-it-works" className="py-24 max-w-7xl mx-auto px-6">
          <div className="text-center mb-20 space-y-4">
            <div className="overline text-sage tracking-[0.25em] font-bold text-xs">Notre Philosophie</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-charcoal">Comment Ceremind rend tout plus simple</h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Steps text timeline */}
            <div className="lg:col-span-7 space-y-12 relative pl-4 border-l border-border-cream/80">
              {[
                { s: "01", t: "Créez votre espace partagé", d: "En 2 minutes, vous avez un lieu privé rien qu’à vous deux. Connectez vos idées et alignez vos envies sans intermédiaires." },
                { s: "02", t: "Invitez et organisez ensemble", d: "Liste d’invités, RSVP, budget partagé, plan de table 3D immersif… tout est centralisé au même endroit en temps réel." },
                { s: "03", t: "Profitez du moment", d: "L'intelligence artificielle gère la logistique lourde. Restez concentrés sur l’essentiel : votre amour et vos invités." }
              ].map((step, i) => (
                <div key={step.s} className="relative pl-8 group">
                  {/* Bullet badge */}
                  <div className="absolute -left-[29px] top-0.5 w-6 h-6 rounded-full bg-white border-2 border-sage flex items-center justify-center text-[10px] font-bold text-sage group-hover:bg-sage group-hover:text-white transition-colors duration-300">
                    {step.s}
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-charcoal mb-2">{step.t}</h3>
                  <p className="text-sm text-charcoal-soft font-light leading-relaxed max-w-xl">{step.d}</p>
                </div>
              ))}
            </div>

            {/* Context Image side */}
            <div className="lg:col-span-5 relative">
              <div className="rounded-3xl overflow-hidden border border-border-cream bg-white p-2 shadow-xl aspect-video lg:aspect-square">
                <img 
                  src={ringsImage} 
                  alt="Échange d'alliances émotionnel et sobre" 
                  className="w-full h-full object-cover rounded-2xl" 
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 4 - Fonctionnalités Anti-Stress */}
        <section id="features" className="py-24 bg-white border-t border-b border-border-cream/40">
          <div className="max-w-4xl mx-auto text-center mb-20 px-6 space-y-4">
            <div className="overline text-sage tracking-[0.25em] font-bold text-xs">Outils & Innovation</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-charcoal">Des outils pensés pour vous apaiser</h2>
            <p className="text-charcoal-soft font-light max-w-xl mx-auto">
              Chaque fonctionnalité est conçue pour simplifier la logistique de votre mariage et alléger votre esprit.
            </p>
          </div>

          <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { t: "Liste d’invités intelligente", d: "Suivi des réponses (RSVP) en direct, gestion des restrictions alimentaires, placements automatiques en un clic.", icon: Users },
              { t: "Budget transparent", d: "Partagé en temps réel entre vous deux. Répartition claire par catégorie, alertes de dépassement et suivi des paiements.", icon: Coins },
              { t: "Plan de table 3D", d: "Visualisez votre salle de réception et modifiez la disposition des invités facilement sans perdre patience ni faire de croquis.", icon: LayoutGrid },
              { t: "Communication guidée", d: "Discussions et notes structurées au sein du couple pour décider sereinement et éviter les malentendus du quotidien.", icon: MessageSquare },
              { t: "Assistant IA Wedding Planner", d: "Un conseiller intelligent disponible à toute heure pour vous inspirer, rédiger vos discours ou résoudre un casse-tête de placement.", icon: Sparkles },
              { t: "Souvenirs & Timeline", d: "Conservez les plus beaux moments et les étapes marquantes avant, pendant et après la plus belle fête de votre vie.", icon: CalendarHeart }
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={f.t} className="p-8 rounded-2xl border border-border-cream/80 hover:border-sage hover:bg-parchment/20 transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-2xl bg-parchment border border-border-cream/60 flex items-center justify-center mb-6 group-hover:bg-sage group-hover:text-white transition-colors duration-300 text-sage">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-charcoal mb-3">{f.t}</h3>
                  <p className="text-sm text-charcoal-soft font-light leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 5 - Témoignages */}
        <section id="testimonials" className="py-24 bg-parchment/40">
          <div className="max-w-4xl mx-auto text-center mb-20 px-6 space-y-4">
            <div className="overline text-sage tracking-[0.25em] font-bold text-xs">Expériences Réelles</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-charcoal">Ils ont organisé leur mariage avec Ceremind</h2>
          </div>

          <div className="max-w-[90rem] mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white p-6 md:p-7 rounded-2xl border border-border-cream/80 shadow-sm relative flex flex-col justify-between hover:shadow-md transition-shadow duration-300">
                <div className="text-terracotta flex gap-1 mb-4">
                  {[...Array(5)].map((_, starIndex) => (
                    <Star key={starIndex} className={`w-3.5 h-3.5 ${starIndex < t.rating ? "fill-current" : "fill-transparent opacity-30"}`} />
                  ))}
                </div>
                <p className="text-charcoal-soft font-light text-[13px] leading-relaxed italic mb-6">
                  "{t.text}"
                </p>
                <div className="mt-auto">
                  <strong className="font-serif text-charcoal block text-sm mb-0.5">— {t.names}</strong>
                  <span className="text-[10px] text-charcoal-soft/60 uppercase tracking-wider font-semibold font-sans block">{t.status}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section Finale - CTA */}
        <section className="py-24 max-w-5xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-br from-rosepoudre/60 via-parchment to-terracotta/20 border border-border-cream p-12 lg:p-20 rounded-3xl space-y-8 shadow-inner">
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-charcoal leading-tight">
              Prêts à vivre cette belle aventure sereinement ?
            </h2>
            <p className="text-charcoal-soft font-light max-w-lg mx-auto text-balance">
              Rejoignez des centaines de couples qui organisent leur mariage en toute tranquillité.
            </p>
            <div className="pt-4">
              {user ? (
                <Link 
                  to="/app" 
                  className="btn-terracotta px-10 py-5 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-transform inline-block"
                >
                  Accéder à notre Espace Mariage
                </Link>
              ) : (
                <Link 
                  to="/register" 
                  className="btn-terracotta px-10 py-5 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-transform inline-block"
                >
                  Créer notre espace mariage gratuitement
                </Link>
              )}
            </div>
          </div>
        </section>

      </main>

      {/* Premium Footer */}
      <footer className="bg-white border-t border-border-cream relative overflow-hidden">
        {/* Subtle top gradient */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sage/30 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-12">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-20 relative z-10">
            
            {/* Colonne Marque */}
            <div className="lg:col-span-5 space-y-8 pr-0 lg:pr-12">
              <img src={logoMain} alt="Ceremind Logo" className="h-12 w-auto object-contain" />
              <p className="text-charcoal-soft font-light text-sm lg:text-base leading-relaxed text-balance">
                L'intelligence artificielle au service de votre grand jour. Organisez, budgétez et célébrez votre union en toute sérénité avec l'assistant de mariage le plus avancé.
              </p>
              
              {/* Newsletter simplifiée */}
              <div className="pt-2">
                <div className="text-xs uppercase tracking-widest font-semibold text-sage mb-3">Lettre d'inspiration</div>
                <div className="flex gap-2">
                  <input type="email" placeholder="Votre email" className="flex-1 px-4 py-2.5 rounded-full bg-parchment/50 border border-border-cream text-sm focus:outline-none focus:border-terracotta transition-colors" />
                  <button className="btn-terracotta px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm hover:scale-[1.02] transition-transform">
                    Rejoindre
                  </button>
                </div>
              </div>
            </div>

            {/* Liens - Groupés pour layout lg */}
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8 pt-4 lg:pt-0">
              {/* Colonne Produit */}
              <div className="space-y-6">
                <h4 className="font-serif text-xl text-charcoal">Le Produit</h4>
                <ul className="space-y-4 text-sm text-charcoal-soft font-light">
                  <li><button type="button" className="hover:text-terracotta hover:translate-x-1 inline-block text-left transition-all duration-300">Fonctionnalités</button></li>
                  <li><button type="button" className="hover:text-terracotta hover:translate-x-1 inline-block text-left transition-all duration-300">Assistant IA</button></li>
                  <li><button type="button" className="hover:text-terracotta hover:translate-x-1 inline-block text-left transition-all duration-300">Tarifs</button></li>
                  <li><button type="button" className="hover:text-terracotta hover:translate-x-1 inline-block text-left transition-all duration-300">Espace Invités</button></li>
                </ul>
              </div>

              {/* Colonne Ressources */}
              <div className="space-y-6">
                <h4 className="font-serif text-xl text-charcoal">Ressources</h4>
                <ul className="space-y-4 text-sm text-charcoal-soft font-light">
                  <li><button type="button" className="hover:text-terracotta hover:translate-x-1 inline-block text-left transition-all duration-300">Le Blog Mariage</button></li>
                  <li><button type="button" className="hover:text-terracotta hover:translate-x-1 inline-block text-left transition-all duration-300">Inspirations</button></li>
                  <li><button type="button" className="hover:text-terracotta hover:translate-x-1 inline-block text-left transition-all duration-300">Centre d'aide (FAQ)</button></li>
                  <li><button type="button" className="hover:text-terracotta hover:translate-x-1 inline-block text-left transition-all duration-300">Devenir Partenaire</button></li>
                </ul>
              </div>

              {/* Colonne Contact */}
              <div className="space-y-6">
                <h4 className="font-serif text-xl text-charcoal">Nous Contacter</h4>
                <ul className="space-y-4 text-sm text-charcoal-soft font-light">
                  <li><a href="mailto:hello@ceremind.ai" className="hover:text-terracotta hover:translate-x-1 inline-block transition-all duration-300">hello@ceremind.ai</a></li>
                  <li><button type="button" className="hover:text-terracotta hover:translate-x-1 inline-block text-left transition-all duration-300">Support Technique</button></li>
                  <li><button type="button" className="hover:text-terracotta hover:translate-x-1 inline-block text-left transition-all duration-300">Presse & Médias</button></li>
                </ul>
              </div>
            </div>

          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-border-cream/80 flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
            <div className="flex items-center gap-2 text-[12px] text-charcoal-soft font-light">
              <span>© {new Date().getFullYear()} Ceremind.</span>
              <span className="hidden md:inline">Tous droits réservés.</span>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-6 text-[11px] uppercase tracking-widest font-semibold text-charcoal-soft/80">
              <button onClick={() => setLegalModal('mentions')} className="hover:text-terracotta transition-colors">Mentions Légales</button>
              <button onClick={() => setLegalModal('privacy')} className="hover:text-terracotta transition-colors">Confidentialité</button>
              <button className="hover:text-terracotta transition-colors">CGV</button>
            </div>
            
            <div className="flex items-center gap-4">
              <button type="button" aria-label="Instagram Ceremind" className="group w-10 h-10 rounded-full bg-parchment flex items-center justify-center text-charcoal hover:bg-terracotta hover:text-white transition-all duration-300 hover:-translate-y-1">
                <Instagram className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </button>
              <button type="button" aria-label="X Ceremind" className="group w-10 h-10 rounded-full bg-parchment flex items-center justify-center text-charcoal hover:bg-terracotta hover:text-white transition-all duration-300 hover:-translate-y-1">
                <Twitter className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </button>
              <button type="button" aria-label="Facebook Ceremind" className="group w-10 h-10 rounded-full bg-parchment flex items-center justify-center text-charcoal hover:bg-terracotta hover:text-white transition-all duration-300 hover:-translate-y-1">
                <Facebook className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>

          {/* Subtle background decoration */}
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-rosepoudre/20 rounded-full blur-3xl -mr-[250px] -mb-[250px] pointer-events-none"></div>
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-sage/10 rounded-full blur-3xl -ml-[200px] -mt-[200px] pointer-events-none"></div>

        </div>
      </footer>
      {/* Legal Modals */}
      {legalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col font-sans">
            <div className="p-6 border-b border-border-cream flex justify-between items-center">
              <h2 className="text-xl font-semibold text-charcoal font-serif">
                {legalModal === 'mentions' ? "Conditions & Mentions Légales" : "Politique de Confidentialité"}
              </h2>
              <button onClick={() => setLegalModal(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-sm leading-relaxed space-y-4 text-charcoal-soft">
              {legalModal === 'mentions' ? (
                <>
                  <p><strong>1. Éditeur du site</strong></p>
                  <p>Le site est édité par Ceremind, plateforme de création de sites de mariage.</p>
                  <p><strong>2. Hébergement</strong></p>
                  <p>Le site est hébergé de manière sécurisée en respectant les normes RGPD. L'hébergeur s'engage à maintenir des standards élevés de sécurité.</p>
                  <p><strong>3. Propriété Intellectuelle</strong></p>
                  <p>Tous les contenus présents sur ce site (images, textes, vidéos) sont soumis aux droits de propriété intellectuelle des mariés ou de la plateforme Ceremind. Toute reproduction sans autorisation est interdite.</p>
                </>
              ) : (
                <>
                  <p><strong>1. Collecte des données</strong></p>
                  <p>Les données collectées sur ce site sont strictement utilisées pour l'organisation des événements et l'utilisation de l'application Ceremind.</p>
                  <p><strong>2. Finalité des données</strong></p>
                  <p>Nous ne vendons ni ne louons vos données personnelles à des tiers. Les informations sont uniquement accessibles pour le bon fonctionnement du service.</p>
                  <p><strong>3. Droits des utilisateurs</strong></p>
                  <p>Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit d'accès, de rectification, de suppression de vos données.</p>
                  <p><strong>4. Cookies</strong></p>
                  <p>Ce site peut utiliser des cookies essentiels au bon fonctionnement et à la sécurité de l'interface.</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
